'use client';

import useMixPanel from '@/hooks/useMixPanel';
import { useRootStore } from '@/providers/root-store-provider';
import { TOrganizationFAQ } from '@/services/api/organization-api';
import React, { useCallback, useEffect } from 'react';
import ChooseDateTimeStep from '../choose-date-time-step/ChooseDateTimeStep';
import ChooseServiceStep from '../choose-service-step/ChooseServiceStep';
import ParticipantsDataStep from '../participants-data-step/ParticipantsDataStep';
import PaymentStep from '../payment-step/PaymentStep';
import Stepper from '../stepper/Stepper';
import ChooseDivisionStep from '../choose-division-step/ChooseDivisionStep';

const NewBookingSection: React.FC<{ organization: TOrganizationFAQ }> = ({
  organization
}) => {
  const Mixpanel = useMixPanel();

  const stepServicesData = useRootStore((store) => store.stepServicesData);
  const currentStep = useRootStore((store) => store.currentStep);
  const organizationId = useRootStore((state) => state.organizationId);
  const setStep = useRootStore((store) => store.setStep);
  const setInIframe = useRootStore((store) => store.setInIframe);
  const cleanStepServicesData = useRootStore(
    (store) => store.cleanStepServicesData
  );
  const setOrganizationId = useRootStore((store) => store.setOrganizationId);
  const isDataExpired = useRootStore((store) => store.isDataExpired);
  const purchaseCompleted = useRootStore((store) => store.purchaseCompleted);
  const setPurchaseCompleted = useRootStore(
    (store) => store.setPurchaseCompleted
  );

  const serviceHasRangeDivisions =
    stepServicesData?.service?.rangeDivisions?.length &&
    stepServicesData?.service?.rangeDivisions?.length > 0;

  const iniFrame = useCallback(() => {
    if (window.location !== window.parent.location) {
      // The page is in an iFrames
      setInIframe(true);
    } else {
      // The page is not in an iFrame
      setInIframe(false);
    }
  }, [setInIframe]);

  useEffect(() => {
    iniFrame();
  }, [iniFrame]);

  useEffect(() => {
    const isExpired = isDataExpired();
    if (isExpired) {
      cleanStepServicesData();
      setStep(1);
    }
    if (purchaseCompleted) {
      setPurchaseCompleted(false);
      cleanStepServicesData();
      setStep(1);
    }
    Mixpanel.track('New Booking Section Viewed', {
      screen: 'New Booking Section',
      organization: organization.name
    });
  }, []);

  useEffect(() => {
    if (organization && organizationId !== organization.id) {
      setStep(1);
      cleanStepServicesData();
      setOrganizationId(organization.id);
    }
  }, [organization]);

  // scroll to top when the step changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  return (
    <div>
      <Stepper totalSteps={4} />
      <div className="mt-8 mb-14 md:container">
        {currentStep === 1 && <ChooseServiceStep organization={organization} />}
        {currentStep === 2 &&
          (serviceHasRangeDivisions ? (
            <ChooseDivisionStep organization={organization} />
          ) : (
            <ChooseDateTimeStep organization={organization} />
          ))}
        {currentStep === 3 && (
          <ParticipantsDataStep organization={organization} />
        )}
        {currentStep === 4 && <PaymentStep organization={organization} />}
      </div>
    </div>
  );
};

export default NewBookingSection;
